// Pricebook price/copy helpers shared by the plan cards and the learn-more drawer.
//
// Two different free-trial offers arrive on the same `pricebook/available` payload and must not
// be conflated:
//   * `metadata.freeTrial30Day` — legacy 30-day trial: the price slot reads "30 days free" and
//     the subtext names the price the subscription renews at.
//   * `metadata.internalFreeTrial` — the retail 32-day trial: the price slot reads "$0*" with
//     "no subscription required", and the page carries the free-trial footnote built from
//     `metadata.advicePrice`. Without this flag the card falls through to the generic path and
//     wrongly advertises the trial as a $0 recurring subscription.
// Mirrors 3Nickels PriceDisplay / InternalFreeTrialFootnote so both sign-up flows describe the
// same offer identically.

export const intervalAbbrev = { MONTH: 'mo', YEAR: 'yr' }

// "$0", "$299", "$14.99" — cents only when the amount actually has them.
export const formatDollars = dollars => {
  const amount = Number(dollars ?? 0)
  return new Intl.NumberFormat('en-us', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  }).format(amount)
}

// Designs style the "$" as its own run, so the amount goes in without it.
export const formatAmount = dollars => formatDollars(dollars).replace('$', '')

// Spelled-out interval for prose; anything other than YEAR bills monthly.
export const intervalWord = intervalType => (intervalType === 'YEAR' ? 'year' : 'month')

export const isFreeTrial30Day = pb => pb?.pricebook?.metadata?.freeTrial30Day === 'true'
export const isInternalFreeTrial = pb => pb?.pricebook?.metadata?.internalFreeTrial === 'true'
export const isFreemium = pb => !!pb?.pricebook?.metadata?.freemium

// The 32-day trial and freemium offers both defer their terms to a footnote, so their price
// carries an asterisk. The legacy 30-day trial states its terms inline instead.
export const showsAsterisk = pb => isInternalFreeTrial(pb) || isFreemium(pb)

// An interval ("/mo") only makes sense for a plain recurring charge.
export const showsInterval = pb => {
  const price = pb?.prices?.[0]
  return (
    (price?.price ?? 0) > 0 &&
    !isFreeTrial30Day(pb) &&
    !isInternalFreeTrial(pb) &&
    !isFreemium(pb) &&
    !!intervalAbbrev[price.intervalType]
  )
}

// Limit retail checkout to offers the member can purchase directly.
export const guidedChoiceOffer = bundles => {
  const sellable = (bundles ?? []).filter(
    bundle =>
      !bundle.pricebook.addOn &&
      bundle.pricebook.productType === 'GUIDED_CHOICE' &&
      bundle.pricebook.paidBy !== 'ORG' &&
      showsInterval(bundle)
  )
  return sellable.find(bundle => bundle.pricebook.defaultPricebook) ?? sellable[0]
}

// Small line under the price.
export const priceSubtext = pb => {
  const price = pb?.prices?.[0]
  if (isFreeTrial30Day(pb)) return 'then $14.99/mo'
  if (isInternalFreeTrial(pb)) return 'no subscription required'
  if ((price?.price ?? 0) === 0) return 'free'
  if (price.intervalType === 'MONTH') return 'auto-renews monthly'
  if (price.intervalType === 'YEAR') return 'auto-renews annually'
  return 'one-time payment'
}

// Footnote sentence for the 32-day trial. The trial length is fixed by the offer; the renewal
// price and interval come from the pricebook. `advicePrice` is what the subscription costs after
// the trial, so fall back to the standard Advice price rather than advertising a $0 renewal.
export const internalFreeTrialTerms = pb =>
  `Subscription not required during free trial. Option to start an auto-renewing subscription of ${formatDollars(
    pb?.pricebook?.metadata?.advicePrice ?? 14.99
  )}/${intervalWord(pb?.prices?.[0]?.intervalType)} during or after 32-day free trial.`
